import { useEffect, useState, type ReactNode } from "react";
import type { Construction, StoreyCeilingAnswer, StoreyFloorAnswer, SurveyRoom } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useSubmit } from "../app/use-submit.js";
import { Failure } from "./Async.jsx";

/**
 * What is under this room and what is over it, answered for the room alone —
 * a flat with another home below or above, a room over a garage, a bedroom
 * under a flat roof. The Ready for design card answers the same question for
 * a whole storey; this is the same answer (survey/storey-fabric.ts), for one
 * room.
 *
 * The temperature of another home or an unheated space is always the
 * designer's: MCS publishes none, so nothing here fills one in.
 */

type FloorKind = StoreyFloorAnswer["kind"];
type CeilingKind = StoreyCeilingAnswer["kind"];

const FLOORS: readonly { readonly kind: FloorKind; readonly label: string }[] = [
  { kind: "solid-ground", label: "Solid ground floor" },
  { kind: "suspended-ground", label: "Suspended timber floor" },
  { kind: "heated-below", label: "Heated room below" },
  { kind: "unheated-below", label: "Unheated space below" },
  { kind: "dwelling-below", label: "Another dwelling below" },
  { kind: "exposed", label: "Outside air below" },
];

const CEILINGS: readonly { readonly kind: CeilingKind; readonly label: string }[] = [
  { kind: "heated-above", label: "Heated room above" },
  { kind: "roof", label: "Roof or loft above" },
  { kind: "dwelling-above", label: "Another dwelling above" },
];

/** What the room's surfaces say is under it now. */
export function currentFloor(room: SurveyRoom): FloorKind | null {
  const floors = room.surfaces.filter((s) => s.kind === "floor");
  if (floors.length === 0) return "heated-below";
  const kinds = new Set(
    floors.map((f): FloorKind | null => {
      switch (f.adjacency) {
        case "ground":
          return "solid-ground";
        case "suspended-floor":
          return "suspended-ground";
        case "adjacent-room":
          return "heated-below";
        case "unheated-space":
          return "unheated-below";
        case "party":
          return "dwelling-below";
        case "external":
          return "exposed";
        default:
          return null;
      }
    }),
  );
  return kinds.size === 1 ? [...kinds][0]! : null;
}

/** What the room's surfaces say is over it now. */
export function currentCeiling(room: SurveyRoom): CeilingKind {
  if (room.surfaces.some((s) => s.kind === "roof" || s.kind === "rooflight")) return "roof";
  if (room.surfaces.some((s) => s.kind === "ceiling" && s.adjacency === "party")) return "dwelling-above";
  return "heated-above";
}

function useConstructionList(element: "floor" | "roof", wanted: boolean): readonly Construction[] | null {
  const { client } = usePortal();
  const [list, setList] = useState<readonly Construction[] | null>(null);
  useEffect(() => {
    if (!wanted || list !== null) return;
    let live = true;
    client.constructions(element).then(
      (rows) => live && setList(rows),
      () => live && setList([]),
    );
    return () => {
      live = false;
    };
  }, [client, element, wanted, list]);
  return list;
}

const isTemperature = (raw: string): boolean => raw.trim() !== "" && Number.isFinite(Number(raw)) && Math.abs(Number(raw)) <= 30;

export function RoomFloorCeiling({
  surveyId,
  room,
  canEdit,
  onSaved,
}: {
  readonly surveyId: string;
  readonly room: SurveyRoom;
  readonly canEdit: boolean;
  readonly onSaved: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const nowFloor = currentFloor(room);
  const nowCeiling = currentCeiling(room);
  const [floor, setFloor] = useState<FloorKind | null>(null);
  const [ceiling, setCeiling] = useState<CeilingKind | null>(null);
  const [floorTemp, setFloorTemp] = useState("");
  const [floorConstruction, setFloorConstruction] = useState("");
  const [ceilingTemp, setCeilingTemp] = useState("");
  const [ceilingU, setCeilingU] = useState("");
  const [roofConstruction, setRoofConstruction] = useState("");
  const floors = useConstructionList("floor", floor === "exposed");
  const roofs = useConstructionList("roof", ceiling === "roof");

  useEffect(() => {
    setFloor(null);
    setCeiling(null);
  }, [room.id]);

  if (room.unheated) return null;

  const saveFloor = (answer: StoreyFloorAnswer) =>
    submit(async () => {
      await client.setRoomFloor(surveyId, room.id, answer);
      setFloor(null);
      onSaved();
    });
  const saveCeiling = (answer: StoreyCeilingAnswer) =>
    submit(async () => {
      await client.setRoomCeiling(surveyId, room.id, answer);
      setCeiling(null);
      onSaved();
    });

  const pickFloor = (kind: FloorKind) => {
    if (kind === "solid-ground" || kind === "suspended-ground" || kind === "heated-below") {
      void saveFloor({ kind });
    } else {
      setFloor(kind);
    }
  };
  const pickCeiling = (kind: CeilingKind) => {
    if (kind === "heated-above") void saveCeiling({ kind });
    else setCeiling(kind);
  };

  const shownFloor = floor ?? nowFloor;
  const shownCeiling = ceiling ?? nowCeiling;

  return (
    <section className="room-fabric" aria-label={`Floor and ceiling of ${room.name}`} data-testid="room-floor-ceiling">
      <p className="room-fabric__title">Floor and ceiling</p>
      <div className="room-fabric__row" role="group" aria-label="What is below this room">
        <span className="small muted">Below</span>
        {FLOORS.map((c) => (
          <button
            key={c.kind}
            type="button"
            className={`design-readiness__choice${shownFloor === c.kind ? " is-chosen" : ""}`}
            aria-pressed={shownFloor === c.kind}
            disabled={!canEdit || busy}
            onClick={() => pickFloor(c.kind)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {floor === "unheated-below" || floor === "dwelling-below" ? (
        <form
          className="room-fabric__form"
          onSubmit={(e) => {
            e.preventDefault();
            if (isTemperature(floorTemp)) void saveFloor({ kind: floor, temperatureC: Number(floorTemp) });
          }}
        >
          <label>
            {floor === "dwelling-below" ? "Temperature of the home below" : "Temperature of the space below"}{" "}
            <input
              type="number"
              step={0.5}
              placeholder="e.g. 18"
              aria-label={floor === "dwelling-below" ? "Temperature of the home below, °C" : "Temperature of the space below, °C"}
              value={floorTemp}
              onChange={(e) => setFloorTemp(e.target.value)}
            />{" "}
            °C
          </label>
          <button type="submit" disabled={busy || !isTemperature(floorTemp)}>
            {busy ? "Saving…" : "Save the floor"}
          </button>
          <button type="button" className="floor-plan__link" onClick={() => setFloor(null)}>
            Cancel
          </button>
        </form>
      ) : null}
      {floor === "exposed" ? (
        <form
          className="room-fabric__form"
          onSubmit={(e) => {
            e.preventDefault();
            if (floorConstruction) void saveFloor({ kind: "exposed", constructionId: floorConstruction });
          }}
        >
          <label>
            Floor construction{" "}
            <select value={floorConstruction} onChange={(e) => setFloorConstruction(e.target.value)}>
              <option value="">{floors === null ? "Loading the list…" : "Choose…"}</option>
              {(floors ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={busy || floorConstruction === ""}>
            {busy ? "Saving…" : "Save the floor"}
          </button>
          <button type="button" className="floor-plan__link" onClick={() => setFloor(null)}>
            Cancel
          </button>
        </form>
      ) : null}

      <div className="room-fabric__row" role="group" aria-label="What is above this room">
        <span className="small muted">Above</span>
        {CEILINGS.map((c) => (
          <button
            key={c.kind}
            type="button"
            className={`design-readiness__choice${shownCeiling === c.kind ? " is-chosen" : ""}`}
            aria-pressed={shownCeiling === c.kind}
            disabled={!canEdit || busy}
            onClick={() => pickCeiling(c.kind)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {ceiling === "dwelling-above" ? (
        <form
          className="room-fabric__form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isTemperature(ceilingTemp)) return;
            const u = Number(ceilingU);
            void saveCeiling({
              kind: "dwelling-above",
              temperatureC: Number(ceilingTemp),
              ...(ceilingU.trim() !== "" && u > 0 && u <= 10 ? { uValue: u } : {}),
            });
          }}
        >
          <label>
            Temperature of the home above{" "}
            <input
              type="number"
              step={0.5}
              placeholder="e.g. 18"
              aria-label="Temperature of the home above, °C"
              value={ceilingTemp}
              onChange={(e) => setCeilingTemp(e.target.value)}
            />{" "}
            °C
          </label>
          <label>
            Ceiling U-value, if known{" "}
            <input
              type="number"
              step={0.01}
              placeholder="optional"
              aria-label="Ceiling U-value, W/m²K"
              value={ceilingU}
              onChange={(e) => setCeilingU(e.target.value)}
            />{" "}
            W/m²K
          </label>
          <button type="submit" disabled={busy || !isTemperature(ceilingTemp)}>
            {busy ? "Saving…" : "Save the ceiling"}
          </button>
          <button type="button" className="floor-plan__link" onClick={() => setCeiling(null)}>
            Cancel
          </button>
        </form>
      ) : null}
      {ceiling === "roof" ? (
        <form
          className="room-fabric__form"
          onSubmit={(e) => {
            e.preventDefault();
            if (roofConstruction) void saveCeiling({ kind: "roof", constructionId: roofConstruction });
          }}
        >
          <label>
            Roof construction{" "}
            <select value={roofConstruction} onChange={(e) => setRoofConstruction(e.target.value)}>
              <option value="">{roofs === null ? "Loading the list…" : "Choose…"}</option>
              {(roofs ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={busy || roofConstruction === ""}>
            {busy ? "Saving…" : "Save the roof"}
          </button>
          <button type="button" className="floor-plan__link" onClick={() => setCeiling(null)}>
            Cancel
          </button>
        </form>
      ) : null}
      {error ? <Failure error={error} what="that answer" /> : null}
    </section>
  );
}
